import { AnnouncementInfo, IncidentInfo, Alert } from '../AnnouncementInfo';
import { App, SignInState } from '../app';
import { CropRect } from '../CropRect';
import { SearchResult, SearchResults, CountsByType } from "../SearchResults";
import { SettingsUpdate } from "../SettingsUpdate";
import { Suggestion } from '../SuggestQueryResults';
import { UserProperties, ActivityResults } from "../UserProperties";
import { UserSettings } from "../UserSettings";
import { TagInfo } from "../TagInfo";
import { EntryValue, postTypes } from '../constants/urlParams';
import { Settings } from '../Settings';
import { UserRole } from '../config/permissions';
import { Operation } from './operation';
import { SpaceId } from '../config/spaces/spaceIds';
import { ExportRestrictStatusType } from '../constants/gdprRequests';
import { AccountType, UserInfo } from '../communication/PlatformActions';
import { ModerationPost, User } from '../Moderation';
import { AISummary, PostId, RootPost } from '../RootPost';
import { OpenServerConfig, RestrictedServerConfig } from '../config/serverConfig';
import { stringFromErrorOrAny } from '../utils/tools';

export enum OperationState {
    Executing = 0,
    Success = 1,
    Failed = 2,

    NotStarted = 3,
}

export interface Json {
    [x: string]: string | number | boolean | Date | Json | JsonArray;
}

export interface JsonArray extends Array<string | number | boolean | Date | Json | JsonArray> { }
export interface JsonObjectArray extends Array<Json> { }

export class VSForMacVersion {
    public ProductDisplayVersion: string = "";
}

// WARNING! enums must not be sub-string of each other, makes testing for list of enums easier in regex
export enum MainView {
    home = "home",
    search = "search",
    user = "user",
    details = "details",
    noinstance = "noinstance",
}

// WARNING! enums must not be sub-string of each other, makes testing for list of enums easier in regex
export enum Dialog {
    none = "none",
    add = "add",
    vote = "vote",
    guidance = "guidance",
    comment = "comment",
    suggest = "suggest",
}

// WARNING! enums must not be sub-string of each other, makes testing for list of enums easier in regex
// e.g. "record" and "recording" is bad, "record" and "recprogress" is OK
export enum RapView {
    describe = "describe",
    record = "record",
    recprogress = "recprogress",
    processing = "processing",
    screenshot = "screenshot",
    submit = "submit",
    result = "result",
    recfailed = "recfailed",
    request = "request",
}

// Problem or suggestion
export type EntryValuePostType = typeof postTypes[number];

export enum ActionRecorderState {
    notStarted = "notStarted",
    started = "started",
    processing = "processing",
    complete = "complete",
    failed = "failed",
    cancelled = "cancelled"
}

export enum ReportType {
    newProblem = "newProblem",
    vote = "vote",
    provideInfoComment = "provideInfoComment",
}

export enum Filter {
    all = 'all',
    unsolved = 'unsolved',
    follow = 'follow',
    currentbuild = 'currentbuild',
    specialItem = '_item', // used when query filters to single item
    specialComment = '_comment', // used when query filters to single item based on a descendant comment
}

export enum StateGroupFilter {
    all = 'all',
    active = 'active',
    closed = 'closed',
}

/// <summary>
/// This is used to decide what type of item to search.
/// Note that all values must be a power of 2 so that bitwise operations work
/// Example (Problem | Idea)
///
/// This enum should be the same as Feedback::DataContracts::SearchQueryInfo::TypesFilter
/// </summary>
export enum TypesFilterFlags {
    defaultProblems = 0, // this is used for older clients that don't send a filter.
    problem = 1,
    idea = 2,
    // tslint:disable-next-line:no-bitwise
    all = problem | idea,
}

export enum Sort {
    active = 'active',
    date = 'date',
    newest = 'newest',
    votes = 'votes',
    relevance = 'relevance',
    recent = 'recent',
}

export enum TimeFilter {
    sixMonths = "6m",
    twelveMonths = "12m",
    eighteenMonths = "18m",
    allTime = "allTime",
}

export enum RapSource {
    sendASmile = "Send a smile",
    developerCommunity = "developer-community",
}

export class VectorSearchInfo {
    public lastSearchId: number = 0; // stores the last search request number;  used to discard old search results if we get them after a newer search finishes
    public lastSearchTimeMsec: number = 0;
    public state: OperationState = OperationState.Executing;
    public errorMessage: string = "";

    public searchTitle: string = "";
    public loadedEverything: boolean = false; // true if last search returned no results, false if some results were returned
    public results: SearchResults = new SearchResults();
}

export class SearchInfo {
    public lastSearchId: number = 0; // stores the last search request number;  used to discard old search results if we get them after a newer search finishes
    public searchtext: string = "";
    public searchTags: TagInfo[] = [];
    public suggestedTags: string[] = [];
    public results: SearchResults = new SearchResults();
    public activityResults: ActivityResults = new ActivityResults();
    public state: OperationState = OperationState.Executing;
    public isLoadingMore?: boolean = false;     // Is it doing a new search or just loading more items for a prior search
    public errorMessage: string = "";
    public lastSearchTimeMsec: number = 0;
    public isLastSearchAdditive: boolean = false; // true if last search is additive (scrolling to load more results), false if first search
    public loadedEverything: boolean = false; // true if last search returned no results, false if some results were returned
    public userName: string = "";
}

// Target field to write/read from when calling the search functionality
export enum SearchInfoTarget {
    all = "all",
    problems = "problems",
    suggestions = "suggestions",
    myActivity = "myActivity",
    myProblems = "myProblems",
    mySuggestions = "mySuggestions",
    myNMIItems = "myNMIItems",
}

export enum SearchTab {
    problems = "problems",
    suggestions = "suggestions",
}

export enum FollowTab {
    all = "all",
    myActivity = "myActivity",
    myProblems = "myProblems",
    mySuggestions = "mySuggestions",
}

export enum EventName {
    setSearchBoxText,
    suggestRecalc,
    rapOpen,
    none,
}

export interface Fetchable<T> {
    lastPromiseId?: number;
    isFetching: boolean;
    hasErrors: boolean;
    data?: T;
    errors?: string[];
}

/** Returns a fetchable object being fetched (e.g. REST API call is in progress and progress bar is shown) */
export function Fetchable_InProgress<T>(): Fetchable<T> {
    return {
        isFetching: true,
        hasErrors: false
    };
}

/** Returns a fetched object, ready to use */
export function Fetchable_Success<T>(data: T): Fetchable<T> {
    return {
        isFetching: false,
        hasErrors: false,
        data: data,
    };
}

/** Returns a fetchable object in failed state (e.g. REST API error) */
export function Fetchable_Error<T>(error: any): Fetchable<T> {
    return {
        isFetching: false,
        hasErrors: true,
        errors: [stringFromErrorOrAny(error)]
    };
}

export const isFetched = <T>(fetchable: Fetchable<T> | null): boolean =>
    fetchable
    && !fetchable.isFetching
    && !fetchable.hasErrors;

export interface PagedList<T> {
    items: T[];
    totalItemsCount: number;
    pageNumber: number;
    pageSize: number;
}

export interface FetchablePagedList<T> extends Fetchable<PagedList<T>> {
}

export interface Map<K extends string | number, V> {
    [key: string]: V;
    [key: number]: V;
}

export type NotificationType = "error" | "warning" | "information";

export interface NotificationInfo {
    type: NotificationType;
    title: string;
    message?: string;
    canDismiss?: boolean;
    isCritical?: boolean;   // Will alert screen reader
    onDismiss?: () => void;
}

export enum PatchType {
    Follow = "Follow",
    Unfollow = "Unfollow",
    Flag = "Flag",
    ConvertNode = "ConvertNode",
    AddNode = "AddNode",
    AddComment = "AddComment",
    ModifyComment = "ModifyComment",
    ModifyNode = "ModifyNode",
    DeleteComment = "DeleteComment",
    AddSolution = "AddSolution",
    ModifySolution = "ModifySolution",
    DeleteSolution = "DeleteSolution",
    ModifyTicket = "ModifyTicket",
    ModifyTicketTags = "ModifyTicketTags",
    VoteOnTicket = "VoteOnTicket",
    VoteOnNode = "VoteOnNode",
    ReactivateNode = "ReactivateNode",
    ModerationUpdatePost = "ModerationUpdatePost",
    UpdateUserSuspensionState = "UpdateUserSuspensionState",
}

export enum ReleaseAction {
    Allow = 0,
    Warn = 1,
    Block = 2,
    Notify = 3,
}

export class AppState {
    // For debugging purposes
    public stateVersion: number = 0;

    public settings: Settings = new Settings();

    public isInReadOnlyMode = false;    // Flag to control whether website is in readonly mode (during maintenance etc.)

    public disconnectedFromNativeProcess: boolean = false;

    public operations: Map<string, Operation> = {};

    /** true to require user authentication to see site, false otherwise.
     * Used during development to restrict showing site only to MSFT employees.
     * Note: if connecting through native WebSocket pipe, this value is set to false,
     * since the native functionality hasn't shipped yet and it won't be hit by search engines/crawlers */
    public mustAuthenticate: boolean = true;

    public globalNotification: NotificationInfo | null = null;
    public contextualNotifications: Map<string, NotificationInfo[] | null> = {};

    public view: MainView = MainView.home; // current view
    public previousView: MainView = MainView.home; // previous view, used to show proper back navigation
    public dialog: Dialog = Dialog.none; // current dialog visible, if any
    public showCloseWarning: boolean = false; // true to show close warning dialog, false otherwise
    public showDialogSysLogs: boolean = false; // true to show system logs confirmation dialog, false otherwise
    public usedFormatting: boolean = false; // true if user used formatting when typing text (e.g. while reporting problem), false otherwise

    public actionRecorderState: ActionRecorderState = ActionRecorderState.notStarted;
    public actionRecorderSafeToCloseVS: boolean = false;      // Indicates whether it is now safe to close VS during a recording session
    public filesBeingProcessed: string[] = [];
    public actionRecorderErrorMessage: string | null = "";
    public diagSessionId: number = 0;     // ID of the current diagnostic session (to help listen to events for latest session only)

    public sort: Sort = Sort.relevance;
    public filter: Filter = Filter.all;
    public typeFilter: TypesFilterFlags = TypesFilterFlags.all;

    public problems: SearchInfo = new SearchInfo();
    public suggestions: SearchInfo = new SearchInfo();
    public myActivity: SearchInfo = new SearchInfo();
    public myProblems: SearchInfo = new SearchInfo();
    public mySuggestions: SearchInfo = new SearchInfo();
    public myNMIItems: SearchInfo = new SearchInfo();
    /** Search results for vector search suggestions on report problem page */
    public vectorSuggestions: VectorSearchInfo = new VectorSearchInfo();

    public ticketsById: Map<number, Fetchable<RootPost>> = {};
    public ticketsByCommentId: Map<number, Fetchable<RootPost>> = {};
    public summaries: Map<number, Fetchable<AISummary>> = {};

    // feature feedback load state
    public featureFeedback: Fetchable<string> | null = null;

    // To keep track of different async requests and accordingly enable/disable UI etc.
    public asyncRequests: Map<string, Fetchable<any>> = {};

    public countsByTypeSearchPage: CountsByType = { idea: 0, problem: 0 };
    public countsByTypeHome: CountsByType = { idea: 0, problem: 0 };

    // true if we ever received search results (even if failure), false if this is the first time searching
    // Used to delay sign in until after we get search results
    public hasSearched: boolean = false;

    public accounts: UserInfo[] | null = null; // accounts available; must be non-null if signInState == SignInState.ChooseAccount
    public userLogo: string | null = ""; // user logo base-64 encoded and ready to display with the 'data:image/png;base64,' prefix
    public userName: string = ""; // user name e.g. "Nikola Mihaylov"
    public userEmail: string = "";
    public userVsid: string = "";
    public userAccountType: AccountType = AccountType.msa;
    public signInState: SignInState = SignInState.Checking;
    public signInErrorMessage: string = "";
    public signInSuccessAction: (() => void) | null = null;
    public selectedItem: SearchResult | null = null;
    public selectedItemId: number | null = null;    // @TODO: Derive from selectedItem.answerHubId probably

    public userRoles: UserRole[] = []; // logged in user's roles
    public isUserBanned: boolean = false;

    /** true if required to show Categories UI, false to hide it. passed as a feature flag from backend */
    public useCategories: boolean = false;

    /** true to show AI summaries, false otherwise */
    public aiSummary: boolean = false;

    public approvedFileExtensionsCsv: string = "";

    // commenting
    public commentText: string = ""; // text typed in the "add comment" box
    public detailAddCommentState: OperationState = OperationState.NotStarted;
    public selectedCommentsExpanded: boolean = false; // true if the top-level comments are expanded for the selected item, false otherwise

    // report a problem view
    public rapView: RapView = RapView.describe;
    public rapCloseable: boolean = true; // used to track if the close button should be available

    public rapPostType: EntryValuePostType = EntryValue.problem;
    public rapReportWhatsDirty: string[] = [];   // Whether the user started editing report. Used to show a dialog if user tries to close window before submitting

    public reportCategory: string = "";

    public suggeststate: OperationState = OperationState.Executing;
    public lastSuggestTimeMsec: number = 0;
    public reportSpaceId: SpaceId = SpaceId.VS;
    public reportTitle: string = "";
    public reportSuggestions: Suggestion[] = [];
    public reportDetail: string = "";
    public reportNextEnabled: boolean = true;
    public reportState: OperationState = OperationState.NotStarted;
    public reportErrorMessage: string = "";
    public reportIncludeScreenshot: boolean = false;
    public reportIncludeSystemLogs: boolean = true;
    public reportIsRegression: boolean = false;
    public reportWorkedInText: string = "";
    public reportSeverityText: string = "";
    public reportResultTicketUrl: string | null = null;
    public submittedPostRecently: boolean = false;

    /** screen recording from website (not psr.exe) */
    public reportIncludeScreenRecording: boolean = false;
    public screenRecording: Blob | null = null; // screen recording blob, if any
    public screenRecordingUploadState: OperationState = OperationState.NotStarted;
    public screenRecordingErrorMessage: string = "";
    public screenRecordingPercentDone: number = 0;

    public problemLoadState: OperationState = OperationState.NotStarted;
    public problemLoadErrorMessage: string = "";

    public showRecordTip: boolean = true; // true to show a suggestion to record repro steps, false otherwise
    public hasRecorded: boolean = false; // true if there was a succesful recording this dialog session, false otherwise

    // Routing message for Microsoft internal users
    public routingMessageShouldShow = false;

    // Enhance Your Vote dialog; note: some properties above shared from report problem view
    public voteState: OperationState = OperationState.NotStarted;
    public evNextEnabled: boolean = true;
    public evComment: string = ""; // text typed in the comment box for "enhance your vote"

    // Need More Info dialog; note: some properties above shared from report problem view
    public nmiState: OperationState = OperationState.NotStarted;
    public nmiNextEnabled: boolean = true;
    public nmiComment: string = ""; // text typed in the comment box for "need more info"
    public nmiCommentsExpanded: boolean = false;

    // files to upload except from screenshots
    public reportFilesToUpload: FileInfo[] = [];

    // system files
    public reportGetSystemFilesState: OperationState = OperationState.NotStarted;
    public reportSystemFilesToUpload: FileInfo[] = [];

    // process ID to collect diagnostics for
    public reportRecordPid: number | null = App.settings?.currentProcessId;
    // processes to show in the Record view, so the user can choose which one to record for
    public reportRunningVsInstances: VSInstanceInfo[] = [];
    // tracks if the record button should be enabled
    public reportRecordbuttonEnabled: boolean = false;
    // tracks if we are recording currently (to show warning on close)
    public reportRecordInprogress: boolean = false;
    // tracks whether the VS instance that started ReportAProblem is still running.
    // If it's not, when we close the Add dialog, we want to show a dialog to the user and force them to open ReportAProblem from a running instace, so we get fresh environment data
    public is_parent_vs_instance_running: boolean = true;
    public settings_update: SettingsUpdate | null = null;

    public event: EventName = EventName.none; // used for sending events to ui for one-time state updates

    public lastAction: string | null; // used for one-time state updates in UI based on action

    // -- crop --
    public reportIsCropping: boolean = false;
    public reportIsCropPromptVisible: boolean = false;
    public reportCropRegion: CropRect | null = null; // crop region in thumbnail space
    public reportCroppedScreenshotDataUrl: string | null = null;

    // -- user settings, stored locally (unlike userProperties retrieved from server) --
    public user_local_settings: UserSettings = new UserSettings();

    // -- user properties (retrieved from server): need more info currently, expandable to other later --
    public userProperties: UserProperties = new UserProperties();

    // -- profile and preferences attachments --
    public profileAttachmentsLoadState: OperationState = OperationState.NotStarted;
    public profileAttachments: AttachmentPostInfo[] | null = null;
    public profileAttachmentsErrorMessage: string = "";

    public deleteAttachmentState: OperationState = OperationState.NotStarted;
    public deleteAttachmentErrorMessage: string = "";

    public archiveCreationState: OperationState = OperationState.NotStarted;
    public archiveCreationErrorMessage: string = "";
    public archiveStatusLoadState: OperationState = OperationState.NotStarted;
    public archiveCurrentStatus: ArchiveStatus | null = null;

    public archiveStatusErrorMessage: string = "";

    public deleteAccountLoadState: OperationState = OperationState.NotStarted;
    public deleteAccountErrorMessage: string = "";

    public userProfileLoadState: OperationState = OperationState.NotStarted;
    public userProfile: UserProfile | null = null;
    public userProfileErrorMessage: string = ""

    public userServerSettingsSaveState: OperationState = OperationState.NotStarted;
    public userServerSettingsSaveErrorMessage: string = "";

    public userServerSettingsLoadState: OperationState = OperationState.NotStarted;
    public userServerSettingsErrorMessage: string = "";
    public userServerSettings: UserServerSettings | null = null;

    // -- announcement info from server (preview and/or release) --
    public announcements: AnnouncementInfo = new AnnouncementInfo();
    public announcementsLoadState: OperationState = OperationState.NotStarted;
    public selectedAnnouncement: 'release' | 'preview' = 'release';
    public announcementErrorMessage: string = "";

    // -- incidents info from server (can be empty) --
    public incidents: IncidentInfo = new IncidentInfo(); // to test: { incidents: [{ title: "test1", url: "https://microsoft.com/inc1" }] }
    public incidentsLoadState: OperationState = OperationState.NotStarted;
    public incidentErrorMessage: string = "";

    // -- Outages --
    public outage: Alert[] = [];
    public outageLoadState: OperationState = OperationState.NotStarted;
    public outageErrorMessage: string = "";

    // -- Custom Outages --
    public customOutage: Alert[] = [];
    public customOutageLoadState: OperationState = OperationState.NotStarted;
    public customOutageErrorMessage: string = "";

    // server config and user data
    public openServerConfig: Fetchable<OpenServerConfig> | null = null;
    public serverConfig: Fetchable<RestrictedServerConfig> | null = null;
    public routingRules: Fetchable<Json> | null = null;
    public auditLogs: Fetchable<Json> | null = null;
    public featureFlags: Fetchable<Json> | null = null;
    public modAdminUsers: Fetchable<Json> | null = null;
    public pipelineStatus: Fetchable<Json> | null = null;
    public gdprStatus: Fetchable<Json> | null = null;
    public userMetadataLoadState: OperationState = OperationState.NotStarted;
    public updateSpaceConfig: Fetchable<void> | null = null; // operation state for creating or editing space config
    public updateWebsiteConfig: Fetchable<void> | null = null; // operation state for creating or editing website config

    // -- Items I Follow --
    public followActiveTab: FollowTab = FollowTab.myProblems;

    // -- Search page --
    public searchActiveTab: SearchTab = SearchTab.problems;

    public releaseActionLoadState: OperationState = OperationState.NotStarted;
    public releaseActionErrorMessage: string = "";
    public releaseActionInfo: ReleaseActionInfo = { edition: "", brand: "", version: "", isPreview: false, isFinalMinor: false, action: ReleaseAction.Allow };

    // in memory state of users which were suspended/unsuspended within this session. Improves user experience by preventing page reloads when user is suspended
    // true => suspended, false => not suspended, null => suspend/unsuspend not yet clicked so use the original state
    public usersUpdatedSuspensionState: Map<number, boolean> = {};

    public usersListForAdminView: FetchablePagedList<User> | null = {
        isFetching: false,
        hasErrors: false,
        data: null,
    };

    public moderationPosts: FetchablePagedList<ModerationPost> | null = {
        isFetching: false,
        hasErrors: false,
        data: null
    };
}

export enum AttachmentType {
    screenRecording = "screenRecording", // user-recorded video from website (not psr.exe)
    recording = "recording",
    screenshot = "screenshot",
    userAttachedFile = "userAttachedFile",
    other = "other",
}

export class FileInfo {
    public name: string;
    public path: string;

    /** Used to differentiate on website client side whether file is "safe" (we generated it) or not.
     * Native client does not use this property and validates files against the actual list of files generated when user clicked Report Problem */
    public isUserFile: boolean;

    public fileType?: AttachmentType;
}

export class AttachmentPostInfo {
    public transferId: string;
    public fileName: string;
    public createdDate: Date;
    public rootPostId?: number;
    public rootPostTitle?: string;
    public rootPostTypeName?: string;
    public postTypeName?: string;
}

export class UserProfile {
    public userName: string;
    public userPhoto: string;
    public vsid: string;
    public email?: string;
    public isSuspended?: boolean;
}

export class VSInstanceInfo {
    // Process ID
    public pid: number;
    // MainWindowTitle
    public title: string;

    constructor(pid: number, title: string) {
        this.pid = pid;
        this.title = title;
    }
}

// TODO: update to remove nullable types where unnecessary
export class ArchiveStatus {
    public CreatedDate?: string;
    public DevCommUserId: number;
    public ErrorMessage?: string;
    public ExpireDate?: string;
    public Id: number;
    public LastModifiedDate?: string;
    public RequestSource?: string;
    public RequestType: number;
    public Status?: ExportRestrictStatusType;
    public StatusText?: string;
    public Vsid: string;
    public ZipFileTotalSizeInMb?: number;
    public ZipFiles?: string[];
}

export class UserServerSettings {
    /** Can be null when update requested to skip updating notifications */
    public notifications: Notifications | null;

}

/** User notification preferences */
export class Notifications {
    /** true to enable all notifications, false to disable all */
    public preference: NotificationPreference;
}

export enum NotificationPreference {
    Off = 0,
    Instant = 1,
    Daily = 2,
    Weekly = 3,
}

export class ReleaseActionInfo {
    public edition: string = "";
    public brand: string = "";
    public version: string = "";
    public isPreview: boolean;
    public isFinalMinor: boolean;
    public action: ReleaseAction = ReleaseAction.Allow; //default to allow
}